import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

interface CollapsibleChartCardProps {
  title: string;
  icon: keyof typeof Ionicons.glyphMap;
  latestValue?: string;
  isLoading?: boolean;
  theme: any;
  isRTL?: boolean;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
}

const CollapsibleChartCard: React.FC<CollapsibleChartCardProps> = ({
  title,
  icon,
  latestValue,
  isLoading = false,
  theme,
  isRTL = false,
  children,
  defaultCollapsed = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
      {/* Header - Always visible, tappable */}
      <TouchableOpacity
        style={[
          styles.header,
          { borderBottomColor: theme.colors.border },
          !isCollapsed && styles.headerExpanded,
        ]}
        onPress={() => setIsCollapsed(!isCollapsed)}
        activeOpacity={0.7}
      >
        <View style={[styles.headerLeft, isRTL && styles.headerLeftRTL]}>
          <Ionicons name={icon} size={18} color={theme.colors.primary} />
          <Text style={[styles.title, { color: theme.colors.text }]}>{title}</Text>
          {latestValue && (
            <View style={[styles.latestBadge, { backgroundColor: theme.colors.primary + '15' }]}>
              <Text style={[styles.latestValue, { color: theme.colors.primary }]}>
                {latestValue}
              </Text>
            </View>
          )}
        </View>
        <Ionicons
          name={isCollapsed ? 'chevron-down' : 'chevron-up'}
          size={20}
          color={theme.colors.textSecondary}
        />
      </TouchableOpacity>

      {/* Content - Conditionally rendered */}
      {!isCollapsed && (
        <View style={styles.content}>
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
              <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
                {isRTL ? 'جاري التحميل...' : 'Loading...'}
              </Text>
            </View>
          ) : (
            children
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    marginBottom: 12,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 0,
  },
  headerExpanded: {
    borderBottomWidth: 1,
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerLeftRTL: {
    flexDirection: 'row-reverse',
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
  },
  latestBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 'auto',
  },
  latestValue: {
    fontSize: 14,
    fontWeight: '700',
  },
  content: {
    padding: 16,
    paddingTop: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    fontSize: 14,
    marginTop: 12,
  },
});

export default CollapsibleChartCard;
